<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, between, numeric } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { helpers } from '@vuelidate/validators'
import DatePicker from '@/components/Applications/Shared/DatePicker.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{ disable?: boolean }>()

const requiredButAllowZero = helpers.withMessage(
  'This field is required',
  (value) => value === 0 || (value !== null && value !== undefined && value !== '')
)

const rules = {
  umsokn: {
    taxDiscount: { required, between: between(0, 100), numeric },
    taxStep: { required },
    useSpouseTaxDiscount: { required },
     spouseTaxDiscount: {
      required: requiredIf(() => umsokn.value?.useSpouseTaxDiscount === 1),
      between: between(0, 100),
      numeric,
    },
  },
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})
</script>

<template>
  <v-row v-if="umsokn">
    <v-col cols="12">
      <h2 class="forms__header">
        {{ t('makalifeyrir_umsokn_step3_header') }}
      </h2>
    </v-col>

    <!-- Tax step radios -->
    <v-col cols="12">
      <v-radio-group
        v-model="umsokn.taxStep"
        :readonly="props.disable"
        hide-details
        :error="v$.umsokn.taxStep?.$error"
        :error-messages="v$.umsokn.taxStep?.$error
          ? [t('makalifeyrir_umsokn_step3_taxstep_error')]
          : []"
      >
        <v-radio :label="t('makalifeyrir_umsokn_step3_taxstep1')" :value="1" />
        <v-radio :label="t('makalifeyrir_umsokn_step3_taxstep2')" :value="2" />
        <v-radio :label="t('makalifeyrir_umsokn_step3_taxstep3')" :value="3" />
      </v-radio-group>
    </v-col>

    <!-- Personal tax reduction -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.taxDiscount"
        :label="t('makalifeyrir_umsokn_step3_persontaxreduction')"
        variant="outlined"
        type="number"
        :readonly="props.disable"
        :error="v$.umsokn.taxDiscount?.$error"
        :error-messages="[
          v$.umsokn.taxDiscount?.required?.$invalid
            ? t('umsokn_PEP_error')
            : '',
          v$.umsokn.taxDiscount?.between?.$invalid
            ? t('makalifeyrir_umsokn_step3_persontaxreduction_between')
            : '',
          v$.umsokn.taxDiscount?.numeric?.$invalid
            ? t('makalifeyrir_umsokn_step3_persontaxreduction_numeric')
            : ''
        ].filter(Boolean)"
      />
    </v-col>

    <!-- From date -->
    <v-col cols="12" md="6">
      <DatePicker
        v-model="umsokn.fromDate"
        :label="t('makalifeyrir_umsokn_step3_datefrom')"
        :disabled="props.disable"
        :error="v$.umsokn.fromDate?.$error"
        :error-messages="v$.umsokn.fromDate?.$error
          ? [t('makalifeyrir_umsokn_step3_fromdate_error')]
          : []"
        :min="new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]"
      />
    </v-col>

    <!-- Spouse tax discount radios -->
    <v-col cols="12">
      <p>{{ t('makalifeyrir_umsokn_step3_usespousetaxdiscount') }}</p>
      <v-radio-group
        v-model="umsokn.useSpouseTaxDiscount"
        :readonly="props.disable"
        hide-details
        :error="v$.umsokn.useSpouseTaxDiscount?.$error"
        :error-messages="[
          v$.umsokn.spouseTaxDiscount?.required?.$invalid
            ? t('umsokn_PEP_error')
            : '',
          v$.umsokn.spouseTaxDiscount?.between?.$invalid
            ? t('makalifeyrir_umsokn_step3_persontaxreduction_between')
            : '',
          v$.umsokn.spouseTaxDiscount?.numeric?.$invalid
            ? t('makalifeyrir_umsokn_step3_persontaxreduction_numeric')
            : ''
        ].filter(Boolean)"
      >
        <v-radio
          :label="t('makalifeyrir_umsokn_step3_usespousetaxdiscount_yes')"
          :value="1"
        />
        <v-radio
          :label="t('makalifeyrir_umsokn_step3_usespousetaxdiscount_no')"
          :value="2"
        />
      </v-radio-group>
    </v-col>

    <!-- Spouse tax discount input -->
    <v-col cols="12" md="6" v-if="umsokn.useSpouseTaxDiscount === 1">
      <v-text-field
        v-model="umsokn.spouseTaxDiscount"
        :label="t('makalifeyrir_umsokn_step3_spousetaxreduction')"
        variant="outlined"
        type="number"
        :readonly="props.disable"
        :error="v$.umsokn.spouseTaxDiscount?.$error"
        :error-messages="[
          v$.umsokn.spouseTaxDiscount?.between?.$invalid
            ? t('makalifeyrir_umsokn_step3_persontaxreduction_between')
            : '',
          v$.umsokn.spouseTaxDiscount?.numeric?.$invalid
            ? t('makalifeyrir_umsokn_step3_persontaxreduction_numeric')
            : ''
        ].filter(Boolean)"
      />
    </v-col>

    <!-- Additional info -->
    <v-col cols="12">
      <p class="forms__paragraph">
        {{ t('makalifeyrir_umsokn_step3_aukaupplysingar') }}
      </p>
      <p>
        {{ t('makalifeyrir_umsokn_step3_aukaupplysingar2') }}
      </p>
    </v-col>
  </v-row>
</template>
